import { createBrowserRouter } from 'react-router-dom';
import Dashboard from './pages/Dashboard.tsx';
import { AutoCleverLogin } from './components/AutoCleverLogin/AutoCleverLogin.tsx';
import { SingleSkill } from './pages/SingleSkill.tsx';
import { LayoutWrapper } from './components/LayoutWrapper/LayoutWrapper.tsx';
import ClassesRosters from './pages/ClassesRosters.tsx';
import NotFound from './pages/NotFound.tsx';
import { Protected } from './Protected.tsx';
import { Roles } from './models/User.ts';
import SettingUp from './pages/SettingUp.tsx';
import Students from './pages/Students.tsx';
import Teachers from './pages/Teachers.tsx';
import Planning from './pages/Planning.tsx';
import Skills from './pages/Skills.tsx';
import AssessmentHistory from './pages/AssessmentHistory.tsx';
import Progress from './pages/Progress.tsx';
import Settings from './pages/Settings.tsx';
import SingleUnit from './pages/SingleUnit.tsx';
import Timeout from './pages/Timeout.tsx';
import ReportHub from './pages/ReportHub.tsx';
import { Assignments } from './pages/Assignments.tsx';
import { Assignment } from './pages/Assignment.tsx';
import './index.scss';
import { Diagnostics } from './pages/Diagnostics.tsx';
import DashboardUnitSelector from './pages/DashboardUnitSelector.tsx';
import Monitoring from './pages/Monitoring.tsx';
import IndividualReport from './pages/IndividualReport.tsx';
import { DemoSingleSkillPage } from './pages/guided-tour/DemoSingleSkillPage.tsx';
import TeacherDiagnosticPreview from './components/TeacherDashboard/Diagnostic/TeacherDiagnosticPreview.tsx';
import TeacherDiagnosticPrepDiagnosticWrapper from './components/TeacherDashboard/Diagnostic/TeacherDiagnosticPrepDiagnosticWrapper.tsx';
import StudentLearnWrapper from './components/TeacherDashboard/Diagnostic/GuidedWalkthroughs/StudentLearnWrapper.tsx';
import MetabaseAccessPending from './pages/MetabaseAccessPending.tsx';
import { AutoClassLinkLogin } from './components/AutoClassLinkLogin/AutoClassLinkLogin.tsx';

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <LayoutWrapper>
        <Dashboard />
      </LayoutWrapper>
    ),
  },
  {
    path: '/units/:unitId',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.INTERNAL_ADMIN, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <SingleUnit />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/skill/:skillCode',
    element: (
      <Protected authorizedRoles={[Roles.STUDENT, Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <SingleSkill />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/guided-tour/:skillCode',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.INTERNAL_ADMIN, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <DemoSingleSkillPage />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/classes-rosters',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <ClassesRosters />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/setting-up',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <SettingUp />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/students/:defaultClass?',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <Students />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/teachers',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <Teachers />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/planning',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <Planning />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/quick-data',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <Monitoring />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/units',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <SingleUnit />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/skills',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <Skills />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/assignments',
    element: (
      <Protected>
        <LayoutWrapper>
          <Assignments />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/assignment/:id',
    element: (
      <Protected>
        <LayoutWrapper>
          <Assignment />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/assessment-history',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <AssessmentHistory />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    // TEAME-38 temporary path while the dashboard is under development
    path: '/pick-unit',
    element: (
      <Protected authorizedRoles={[Roles.STUDENT]}>
        <LayoutWrapper>
          <DashboardUnitSelector isInferred={true} setIsInferred={() => {}} />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/progress',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <Progress />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/progress/:studentId/:cids?',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <IndividualReport />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/settings',
    element: (
      <Protected authorizedRoles={[Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <Settings />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/reports-hub',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <ReportHub />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/timeout',
    element: (
      <Protected authorizedRoles={[Roles.STUDENT, Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <Timeout />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/diagnostics',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.NETWORK_ADMIN, Roles.SITE_ADMIN]}>
        <LayoutWrapper>
          <Diagnostics />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/prep-diagnostics',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.SITE_ADMIN, Roles.NETWORK_ADMIN]}>
        <LayoutWrapper>
          <TeacherDiagnosticPrepDiagnosticWrapper />
        </LayoutWrapper>
      </Protected>
    ),
  },

  {
    path: '/how-students-learn',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.SITE_ADMIN, Roles.NETWORK_ADMIN]}>
        <LayoutWrapper>
          <StudentLearnWrapper />
        </LayoutWrapper>
      </Protected>
    ),
  },

  {
    path: '/diagnostic-preview',
    element: (
      <Protected authorizedRoles={[Roles.TEACHER, Roles.SITE_ADMIN, Roles.NETWORK_ADMIN]}>
        <LayoutWrapper>
          <TeacherDiagnosticPreview />
        </LayoutWrapper>
      </Protected>
    ),
  },
  {
    path: '/metabase-access-pending',
    element: (
      <LayoutWrapper>
        <MetabaseAccessPending />
      </LayoutWrapper>
    ),
  },
  {
    path: '/clever-login',
    element: (
      <LayoutWrapper>
        <AutoCleverLogin />
      </LayoutWrapper>
    ),
  },
  {
    path: '/classlink-login',
    element: (
      <LayoutWrapper>
        <AutoClassLinkLogin />
      </LayoutWrapper>
    ),
  },
  {
    path: '*',
    element: (
      <LayoutWrapper>
        <NotFound />
      </LayoutWrapper>
    ),
  },
]);

export default router;
